/*
 *   This software or document includes material copied from or derived from
 *   - https://www.w3.org/TR/wai-aria-practices/examples/dialog-modal/dialog.html.
 *
 *   Copyright © 2018 W3C® (MIT, ERCIM, Keio, Beihang).
 */

const ATTR_CONTROLS = 'aria-controls';
const ATTR_EXPANDED = 'aria-expanded';
const ATTR_HIDDEN = 'aria-hidden';
const ATTR_CONTROLLED_ATTR = 'data-klt-u-controlled-attr';
const ATTR_CONTROL_GROUP = 'data-klt-u-controlled-group';
const ATTR_POPUP_TRIGGER = 'data-klt-u-popup';

export const ATTR_CLASS_EXPANDED_OPTION = '--expanded';
export const ATTR_CLASS_UNEXPANDED_OPTION = '--unexpanded';

let initalized = false;

let patternAstCache = {};

export interface Controller {
    trigger: Element;
    target: Element;
    group?: string;
    controlledAttr: {
        attr: string;
        default: string;
    };
}

interface PopupTriggerOptions {
    minWidth?: number;
    popup?: {
        url: string;
        width: number;
        height: number;
    };
}

export interface RegexTree {
    type: 'RegexExp' | 'group' | 'repetition' | 'characterClass';

    /**
     * The matched value
     */
    value?: string;

    /**
     * The original expression that matched
     */
    match?: string;

    /**
     * Number of repetition, in case of type 'repetition'
     */
    number?: string;

    /**
     * Nested expressions
     */
    expressions?: RegexTree[];
}

const getControlledElement = (elementId: string) => document.getElementById(elementId);
const getControllerElements = (selector: string) => {
    return Array.from(document.querySelectorAll(selector)).map<Controller>((trigger) => {
        const group = getControlGroup(trigger);
        const elementIdToControl = trigger.getAttribute(`${ATTR_CONTROLS}`);
        const target = getControlledElement(elementIdToControl);
        const controlledAttr = getControlledAttrOptions(target);

        return {
            trigger,
            target,
            group,
            controlledAttr,
        };
    });
};

export function init(force: boolean = false): boolean {
    try {
        if (!initalized || force) {
            const controllers = getControllerElements(`[${ATTR_CONTROLS}]`);
            initControlHandler(controllers);
            initPopupHandler();
            initalized = true;
        }
    } catch (e) {
        console.error(`Couldn't initalize utility module.`, e);
        initalized = false;
    } finally {
        return initalized;
    }
}

export function getControlGroup(trigger: Element) {
    return trigger.getAttribute(`${ATTR_CONTROL_GROUP}`);
}

/**
 * Makes all elements with aria-controls react to interactions be expand and hide elements.
 * The data-klt-u-controlled-group attribute is an additional extension to assign multiple extanding elements
 * to one group. When set, only one element can be expanded in the same group.
 *
 * Additionally, to foster progressive enhancement, whenever something to be enabled, disabled is controlled by
 * JavaScript and could not be used in case JavaScript fails, for example in the case of enabling/disabling elements,
 * we keep them enabled first and as soon as the page is loaded and JavaScript could init successfully, we disabled it based
 * on the information given with the data-klt-u-controlled-attr.
 *
 * `data-klt-u-controlled-attr` can take a string of the property we're going to toggle or a json object with the key (attribute) and value
 * which should be applied per default. See the second example below, where we disable the text field as initial value as soon as JavaScript
 * was loaded successfully. We can't add the "disabled" property directly first-hand as when JavaScript fails, the field would always be disabled.
 *
 * @deprecated use Stimulus controller instead
 *
 * @example
 * ```html
 * <button type="button" aria-controls="element-1" class="button-class button-class--unexpanded" data-klt-u-controlled-group="group-id">Expand</button>
 * <div id="element-1" aria-hidden="true">Hello world</div>
 * ```
 * @example
 * ```html
 * <button type="button" aria-controls="element-1" class="button-class button-class--unexpanded" data-klt-u-controlled-group="group-id">Enable</button>
 * <input type="text" id="element-1" data-klt-u-controlled-attr='{"disabled": "disabled"}' />
 * ```
 */
export function initControlHandler(
    controllers: Controller[],
    callback?: (controller: Controller, state: boolean) => void,
) {
    controllers.forEach((controller) => {
        if (controller.target) {
            const controlledAttr = controller.controlledAttr || getControlledAttrOptions(controller.target);
            const initialState = getControlStateByValue(controlledAttr.default, controlledAttr.attr);
            setControlState(controller, initialState, controlledAttr.attr, controllers, callback);

            if (
                controller.trigger instanceof HTMLButtonElement &&
                controller.trigger.getAttribute('type') === 'button'
            ) {
                controller.trigger.addEventListener('click', function (event) {
                    event.stopPropagation();
                    handleToggleControlStateChangeTrigger(controller, controllers, callback);
                });
            } else if (
                controller.trigger instanceof HTMLInputElement &&
                controller.trigger.getAttribute('type') === 'checkbox'
            ) {
                controller.trigger.addEventListener('change', function (event) {
                    event.stopPropagation();
                    handleToggleControlStateChangeTrigger(controller, controllers, callback);
                });
            }
        }
    });
}

export function initPopupHandler(selector: string = `[${ATTR_POPUP_TRIGGER}]`) {
    const popupTriggerElements = document.querySelectorAll(selector);

    if (popupTriggerElements.length > 0) {
        popupTriggerElements.forEach((element) => element.addEventListener('click', popupTriggerClickHandler));
    }
}

function handleToggleControlStateChangeTrigger(
    controller: Controller,
    controllers: Controller[],
    callback?: (controller: Controller, state: boolean) => void,
): boolean {
    const active = getControlStateByController(controller);
    setControlState(controller, !active, controller.controlledAttr.attr, controllers, callback);
    return !active;
}

/**
 * Takes the controller, checks the target and returns true or false based on the current state of the target.
 * e.g. if the target is configured to get 'hidden'' controlled, 'hidden' is considered inactive,
 * thus 'false', and the function would return false if this is the current case for the given controller.
 */
function getControlStateByController(controller: Controller): boolean {
    let active: boolean;

    // Handle active state in the way that active always means, the element is available for interaction
    if (controller.controlledAttr.attr === 'disabled') {
        active = !(controller.target as HTMLInputElement).disabled;
    } else if (controller.controlledAttr.attr === 'hidden') {
        active = !(controller.target as HTMLElement).hidden;
    } else if (controller.target.hasAttribute(ATTR_HIDDEN)) {
        active = !(controller.target.getAttribute(ATTR_HIDDEN) === 'true');
    } else if (controller.trigger.hasAttribute(ATTR_EXPANDED)) {
        active = controller.trigger.getAttribute(ATTR_EXPANDED) === 'true';
    }

    return active;
}

/**
 * Returns true or false based on whether the value for the given attribute is considered as an active or inactive state.
 * e.g. hidden or disabled="disabled" are considered inactive, thus 'false'
 * @param value Value of an HTML attribute
 * @param attr The HTML attribute to check
 */
function getControlStateByValue(value: string | boolean, attr: string): boolean {
    let active: boolean;

    // Handle active state in the way that active always means, the element is available for interaction
    if (attr === 'disabled') {
        active = value !== true && value !== 'disabled';
    } else if (attr === 'hidden') {
        active = value !== true;
    } else if (attr === 'aria-hidden') {
        active = value !== 'true';
    } else if (attr === 'aria-expanded') {
        active = value !== 'false';
    }

    return active;
}

function setControlState(
    controller: Controller,
    activate: boolean,
    attr: string,
    controllers: Controller[] = [],
    callback?: (controller: Controller, state: boolean) => void,
) {
    const controlElementClasses = controller.trigger.className;
    const controlElementFirstClass = controlElementClasses ? controlElementClasses.split(' ')[0] : null;

    switch (attr) {
        case ATTR_HIDDEN:
            controller.target.setAttribute(attr, activate ? 'false' : 'true');
            controller.trigger.setAttribute(ATTR_EXPANDED, activate ? 'true' : 'false');

            if (controlElementFirstClass) {
                controller.trigger.classList.add(
                    activate
                        ? controlElementFirstClass + `${ATTR_CLASS_EXPANDED_OPTION}`
                        : controlElementFirstClass + `${ATTR_CLASS_UNEXPANDED_OPTION}`,
                );
                controller.trigger.classList.remove(
                    activate
                        ? controlElementFirstClass + `${ATTR_CLASS_UNEXPANDED_OPTION}`
                        : controlElementFirstClass + `${ATTR_CLASS_EXPANDED_OPTION}`,
                );
            }

            break;
        case ATTR_EXPANDED:
            controller.trigger.setAttribute(attr, activate ? 'true' : 'false');
            break;
        case 'disabled':
            if (activate) {
                controller.target.removeAttribute('disabled');
            } else {
                controller.target.setAttribute('disabled', 'disabled');
            }
            break;
        case 'hidden':
            // With hidden, 'active' in this case means visible and inactive means 'hidden'
            if (activate) {
                (controller.target as HTMLElement).hidden = false;
            } else {
                (controller.target as HTMLElement).hidden = true;
            }

            if (controller.trigger.hasAttribute('aria-expanded')) {
                controller.trigger.setAttribute('aria-expanded', activate ? 'true' : 'false');
            }
            break;
        default:
            controller.target.setAttribute(attr, activate ? 'true' : 'false');
    }

    if (callback) {
        callback(controller, activate);
    }

    // If part of a group and activate, deactivate all other elements in the group
    if (controllers.length > 0 && controller.group && activate) {
        controllers.forEach((otherController) => {
            if (otherController !== controller && otherController.group === controller.group) {
                setControlState(otherController, false, attr);
                if (callback) {
                    callback(otherController, false);
                }
            }
        });
    }
}

function popupTriggerClickHandler(e: Event) {
    const element = e.currentTarget as HTMLElement;
    const options = getPopupTriggerValues(element);

    if (!options) {
        console.error('No valid options given for popup trigger');
        return;
    }

    if (options.minWidth) {
        const viewportWidth = document.documentElement.clientWidth;

        if (viewportWidth && options.minWidth <= viewportWidth) {
            e.preventDefault();
            openPopup(options.popup.url, options.popup);
        }
    }
}

function openPopup(url: string, features: { width: number; height: number }) {
    window.open(url, 'popup', `width=${features.width},height=${features.height}`);
}

function getPopupTriggerValues(element: HTMLElement): PopupTriggerOptions {
    const values = element.getAttribute(ATTR_POPUP_TRIGGER);
    const url = element.getAttribute('href');

    if (values && url) {
        const options = JSON.parse(values) as PopupTriggerOptions;
        options.popup.url = url;
        return options;
    }

    return null;
}

/**
 * Parses <element attribute="attr" /> and <element attribute='{"attr": "defaultValue"}' /> on the given element
 * and returns the options.
 */
function getControlledAttrOptions(element: Element): Controller['controlledAttr'] {
    let controlledAttrValue = element.getAttribute(ATTR_CONTROLLED_ATTR);

    if (!controlledAttrValue) {
        console.info(
            `klt/util: Initalizing an aria-controls without data-klt-u-controlled-attr set on the target. Defaulting to 'disabled' attribute.`,
            element,
        );
        controlledAttrValue = 'disabled';
    }

    // Value can then be either a key/value or just a string. With key/avlue pair you can define the default value.
    // We try to parse it here and in case parsing fails, we know the value is a string and not an JSON object
    try {
        const options = JSON.parse(controlledAttrValue);

        // We don't care if the JSON has more keys. This utility function can only handle one attribute connection.
        const attr = Object.keys(options)[0];
        return {
            attr,
            default: options[attr],
        };
    } catch (e) {
        // No catch. We simply try parsing it as object and return it if it works.
        // console.log(e);
    }

    const currentVal = element.getAttribute(controlledAttrValue);
    return {
        attr: controlledAttrValue,
        default: currentVal,
    };
}

/**
 * Takes data, a HTMLElement containing ${my.variable} placeholders,
 * renders the data into the template and then applies the result
 * to the given target element.
 */
export function bindDataToTemplate(data, template: HTMLElement | Node): string {
    // Matches all template literals e.g. ${my.variable}
    // and outputs two named capturing groups, 'literal' for
    // the whole match and 'key' for just the key e.g. my.variable.
    const regex = /(?<literal>\$\{(?<key>[\w.]+)\})/g;

    // Search and replace all template literals with the value in the given
    // data object based on the matching key.
    // @ts-ignore
    const output = template.outerHTML.replaceAll(regex, (_match, _a, _b, _c, _d, { key }) => {
        const value = resolve(data, key);
        return value !== undefined ? value : '';
    });
    return output;
}

/**
 * Resolve object value with dot notation e.g. "data.contentItems.name".
 * Does only work with objects, not arrays.
 */
export function resolve(object, key) {
    return key.split('.').reduce((acc, part) => (acc = acc ? acc[`${part}`] : ''), object);
}

/**
 * Parse a regex "rule" to a simplified high-level AST.
 * Only supports groups and character length definitions.
 * Packages like regexp-tree are way too big to use in a web project
 */
export function toRegexAST(regexString: string): RegexTree {
    if (patternAstCache[regexString]) {
        return patternAstCache[regexString];
    }

    /**
     * Find capturing groups
     */
    const regexMatchCaptureGroups = new RegExp(/\(([^)]+)\)/, 'g');

    /**
     * Find character length definitions after characterClasses e.g. [a-Z]{5}
     */
    const regexMatchRepetition = new RegExp(/(\[[^\]]+\])(\{([^\}]+)\})/, 'g');

    /**
     * Find character class definitions e.g. [a-Z]
     */
    const regexMatchCharacterClass = new RegExp(/\[([^\]]+)\]/, 'g');

    const checkRepetitionMatches = (reg: string): RegexTree[] | null => {
        let results: RegexTree[] = [];
        let matches;
        while ((matches = regexMatchRepetition.exec(reg)) !== null) {
            results.push({
                type: 'repetition',
                expressions: [],
                match: matches[0],
                value: matches[1],
                number: matches[3],
            });
        }

        return results.length ? results : null;
    };

    const checkGroupMatches = (reg: string): RegexTree[] | null => {
        let results: RegexTree[] = [];
        let matches;
        while ((matches = regexMatchCaptureGroups.exec(reg)) !== null) {
            results.push({
                type: 'group',
                expressions: [],
                match: matches[0],
                value: matches[1],
            });
        }

        return results.length ? results : null;
    };

    const checkCharacterClassMatches = (reg: string): RegexTree[] | null => {
        let results: RegexTree[] = [];
        let matches = null;
        while ((matches = regexMatchCharacterClass.exec(reg)) !== null) {
            results.push({
                type: 'characterClass',
                expressions: [],
                match: matches[0],
                value: matches[1],
            });
        }

        return results.length ? results : null;
    };

    const check = (reg: string, currentTree?: RegexTree): RegexTree => {
        const result: RegexTree = currentTree || {
            type: 'RegexExp',
            expressions: [],
        };

        const checks = [checkGroupMatches, checkRepetitionMatches, checkCharacterClassMatches];

        checks.some((fn) => {
            let matches = fn(reg);
            if (matches) {
                matches.forEach((match) => {
                    match = check(match.value, match);
                    result.expressions.push(match);
                });

                return true;
            }

            return false;
        });

        return result;
    };

    const ast = check(regexString);
    patternAstCache[regexString] = ast;

    return ast;
}

export function validateByRegexAst(regexAst: RegexTree, value: string, type: RegexTree['type']): boolean {
    if (regexAst.expressions?.length) {
        return regexAst.expressions.reduce((previousValue, regexp) => {
            return validateByRegexAst(regexp, value, type) === true || previousValue;
        }, false);
    } else if (regexAst.type === type) {
        switch (regexAst.type) {
            case 'repetition':
                return value.length === parseInt(regexAst.number);
            default:
                // Add start/end of word binding and the '+' to match more than one character.
                // This makes the regex to only match when the whole value given matches. Otherwise
                // It would yield a false positive when a single character matches.
                return new RegExp(`^${regexAst.match}+$`).test(value);
        }
    }

    return false;
}

/**
 * Get list of all video inputs
 */
export async function getCameraDevices() {
    const cameraDevices = await navigator.mediaDevices.enumerateDevices();
    return cameraDevices.filter((device) => device.kind === 'videoinput');
}

/**
 * Check if browser has permissions to use user camera
 */
export async function hasCameraPermissions() {
    try {
        const test = await await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment' },
        });
        return true;
    } catch (e) {
        console.info(e);
        return false;
    }
}

/**
 * Checks if CSS Flexbox Gaps are supported by the browser
 */
export function supportsCSSFlexBoxGap() {
    // Create a flex container with row-gap set
    const flex = document.createElement('div');
    flex.style.display = 'flex';
    flex.style.flexDirection = 'column';
    flex.style.rowGap = '1px';
    flex.style.zIndex = '-1000';
    flex.style.position = 'absolute';

    // Create two, elements inside it
    flex.append(document.createElement('div'), document.createElement('div'));

    // Append to the DOM (needed to obtain scrollHeight)
    document.body.append(flex);

    // Flex container should be 1px high due to the row-gap
    const isSupported = flex.scrollHeight === 1;
    flex.parentNode.removeChild(flex);

    return isSupported;
}

/**
 * Converts bytes to a human friendly format
 */
export function humanFileSize(size: number): string {
    var i = Math.floor(Math.log(size) / Math.log(1024));
    return Number((size / Math.pow(1024, i)).toFixed(2)) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
}
